import BlogApi from "../../api/BlogApi";
import type {
  IFaqData,
  IFaqResponse,
  IBlogData,
  IBlogParams,
} from "../../api/BlogApi";

const blogApiService = BlogApi.getInstance();

/**
 * Calculate estimated reading time based on content
 * @param content HTML content of the blog
 * @returns Estimated reading time in minutes
 */
export const calculateReadTime = (content: string): number => {
  // Remove HTML tags
  const plainText = content.replace(/<[^>]*>/g, "");

  // Average reading speed (words per minute)
  const wordsPerMinute = 200;

  // Count words
  const wordCount = plainText.trim().split(/\s+/).length;

  // Calculate reading time
  const readingTime = Math.ceil(wordCount / wordsPerMinute);

  // Return at least 1 minute
  return Math.max(1, readingTime);
};

/**
 * Generate a summary from the blog content
 * @param content HTML content of the blog
 * @returns A brief summary
 */
export const getSummary = (content: string): string => {
  // Remove HTML tags
  const plainText = content.replace(/<[^>]*>/g, "");

  // Extract first few sentences (up to 200 chars)
  const summary = plainText.substring(0, 200);

  // Add ellipsis if truncated
  return summary.length < plainText.length ? `${summary}...` : summary;
};

/**
 * Suggest keywords based on title and content
 * @param title Blog title
 * @param content Blog content
 * @returns Array of suggested keywords
 */
export const suggestKeywords = (title: string, content: string): string[] => {
  // This is a simplified version. In a real app, you'd use NLP or AI for better suggestions

  // Combine title and content
  const text = `${title} ${content}`;

  // Convert to lowercase
  const lowercaseText = text.toLowerCase();

  // Split into words
  const words = lowercaseText.split(/\W+/);

  // Common stopwords to exclude
  const stopwords = [
    "a",
    "an",
    "the",
    "and",
    "or",
    "but",
    "in",
    "on",
    "at",
    "to",
    "for",
    "with",
    "by",
    "about",
    "as",
    "is",
    "are",
    "was",
    "were",
    "be",
    "been",
    "being",
    "have",
    "has",
    "had",
    "do",
    "does",
    "did",
    "will",
    "would",
    "shall",
    "should",
    "can",
    "could",
    "of",
    "from",
    "it",
    "its",
    "this",
    "that",
    "these",
    "those",
    "i",
    "you",
    "he",
    "she",
    "we",
    "they",
  ];

  // Filter out stopwords and count word frequency
  const wordCounts: { [key: string]: number } = {};
  words.forEach((word) => {
    if (word.length > 3 && !stopwords.includes(word)) {
      wordCounts[word] = (wordCounts[word] || 0) + 1;
    }
  });

  // Sort by frequency and get top words
  const sortedWords = Object.entries(wordCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map((entry) => entry[0]);

  // Tax-related keywords to include (domain-specific for this app)
  const taxKeywords = [
    "tax",
    "taxation",
    "income",
    "deduction",
    "credit",
    "refund",
    "filing",
    "irs",
    "audit",
    "return",
    "expense",
    "financial",
    "planning",
    "investment",
  ];

  // Include domain-specific keywords that appear in the text
  const domainKeywords = taxKeywords
    .filter(
      (keyword) =>
        lowercaseText.includes(keyword) && !sortedWords.includes(keyword)
    )
    .slice(0, 5);

  return [...sortedWords, ...domainKeywords];
};

/**
 * Convert a string to a URL-friendly slug
 * @param text Text to convert to slug
 * @returns URL-friendly slug
 */
export const convertToSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with a single one
    .trim();
};

/**
 * Calculate SEO score based on various factors
 * @param blogData Blog data
 * @returns Score between 0-100
 */
export const calculateSeoScore = (blogData: IBlogData): number => {
  let score = 0;
  const maxScore = 100;

  // Check title length (40-60 chars is ideal)
  if (blogData.title) {
    const titleLength = blogData.title.length;
    if (titleLength >= 40 && titleLength <= 60) {
      score += 20;
    } else if (titleLength >= 30 && titleLength <= 70) {
      score += 10;
    }
  }

  // Check summary length (120-160 chars is ideal)
  if (blogData.summary) {
    const descLength = blogData.summary.length;
    if (descLength >= 120 && descLength <= 160) {
      score += 20;
    } else if (descLength >= 80 && descLength <= 200) {
      score += 10;
    }
  }

  // Check tags
  if (blogData.tags && blogData.tags.length >= 3) {
    score += 15;
  }

  // Check content length (longer is usually better for SEO)
  if (blogData.content) {
    const contentLength = blogData.content.replace(/<[^>]*>/g, "").length;
    if (contentLength >= 1500) {
      score += 25;
    } else if (contentLength >= 800) {
      score += 15;
    } else if (contentLength >= 300) {
      score += 5;
    }
  }

  // Check for image
  if (blogData.featuredImage) {
    score += 10;
  }

  // Check for internal/external links (simplified)
  if (
    blogData.content &&
    (blogData.content.includes("<a href=") ||
      blogData.content.includes("href="))
  ) {
    score += 10;
  }

  return Math.min(score, maxScore);
};

// Get all blogs with optional pagination and filtering
export const getBlogs = async (page = 1, limit = 10, category?: string) => {
  try {
    const params: IBlogParams = {
      offset: (page - 1) * limit,
      count: limit,
    };

    if (category) {
      params.category = category;
    }

    return await blogApiService.getBlogs(params);
  } catch (error) {
    console.error("Error fetching blogs:", error);
    throw error;
  }
};

// Get a single blog by slug
export const getBlogBySlug = async (slug: string) => {
  try {
    return await blogApiService.getBlogBySlug(slug);
  } catch (error) {
    console.error(`Error fetching blog with slug ${slug}:`, error);
    throw error;
  }
};

// Create a new blog
export const createBlog = async (blogData: IBlogData) => {
  try {
    return await blogApiService.createBlog(blogData);
  } catch (error) {
    console.error("Error creating blog:", error);
    throw error;
  }
};

// Update an existing blog
export const updateBlog = async (id: string, blogData: IBlogData) => {
  try {
   
    return await blogApiService.updateBlog(id, blogData);
  } catch (error) {
    console.error(`Error updating blog with id ${id}:`, error);
    throw error;
  }
};

// Delete a blog
export const deleteBlog = async (id: string) => {
  try {
    const response = await blogApiService.deleteBlog(id);
    return response;
  } catch (error) {
    console.error(`Error deleting blog with id ${id}:`, error);
    throw error;
  }
};

// Delete a blog by slug
export const deleteBlogBySlug = async (slug: string) => {
  try {
    // Get blog ID from slug first
    const blog = await blogApiService.getBlogBySlug(slug);
    if (!blog || !blog.id) {
      throw new Error(`Blog with slug ${slug} not found or has no ID`);
    }

    // Then delete by ID
    return await deleteBlog(blog.id.toString());
  } catch (error) {
    console.error(`Error deleting blog with slug ${slug}:`, error);
    throw error;
  }
};

// Upload blog image
export const uploadBlogImage = async (imageFile: File) => {
  try {
    return await blogApiService.uploadBlogImage(imageFile);
  } catch (error) {
    console.error("Error uploading blog image:", error);
    throw error;
  }
};

// Get blog statistics
export const getBlogStats = async () => {
  try {
    return await blogApiService.getBlogStats();
  } catch (error) {
    console.error("Error fetching blog statistics:", error);
    throw error;
  }
};

// Get blog categories
export const getBlogCategories = async () => {
  try {
    return await blogApiService.getBlogCategories();
  } catch (error) {
    console.error("Error fetching blog categories:", error);
    throw error;
  }
};

// Toggle blog publish status
export const publishBlog = async (
  id: string,
  status:number,
  updated_by:string
) => {
  try {
    status=status?1:0;
    return await blogApiService.updateBlogStatus(id, status,updated_by);
  } catch (error) {
    console.error(
      `Error toggling publish status for blog with ID ${id}:`,
      error
    );
    throw error;
  }
};

// Get all FAQs with pagination and filtering
export const getFaqs = async (page = 1, limit = 10, status?: string) => {
  try {
    return await blogApiService.getFaqs(page, limit, status);
  } catch (error) {
    console.error("Error fetching FAQs:", error);
    throw error;
  }
};

// Get FAQs for a specific blog
export const getBlogFaqs = async (blogId: string) => {
  try {
    return await blogApiService.getBlogFaqs(blogId);
  } catch (error) {
    console.error(`Error fetching FAQs for blog ID ${blogId}:`, error);
    throw error;
  }
};

// Create a new FAQ item
export const createFaq = async (faqData: IFaqData) => {
  try {
    return await blogApiService.createFaq(faqData);
  } catch (error) {
    console.error("Error creating FAQ:", error);
    throw error;
  }
};

// Update an existing FAQ item
export const updateFaq = async (faqId: string, faqData: IFaqData) => {
  try {
    return await blogApiService.updateFaq(faqId, faqData);
  } catch (error) {
    console.error(`Error updating FAQ with ID ${faqId}:`, error);
    throw error;
  }
};

// Delete a FAQ item
export const deleteFaq = async (faqId: string) => {
  try {
    return await blogApiService.deleteFaq(faqId);
  } catch (error) {
    console.error(`Error deleting FAQ with ID ${faqId}:`, error);
    throw error;
  }
};

// Re-export the interfaces as types
export type { IFaqData, IFaqResponse, IBlogData, IBlogParams };
